import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  limit,
  startAfter,
  serverTimestamp,
  increment,
  onSnapshot,
  type DocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import type { Listing, CreateListingData, ListingStatus } from '@/types/listing'
import { getCategoryById } from '@/utils/categories'

const LISTINGS_COLLECTION = 'listings'

// ── NZ city-to-region mappings for smarter location matching ──
const NZ_CITY_TO_REGION: Record<string, string> = {
  // Auckland region
  'auckland': 'auckland',
  'manukau': 'auckland',
  'waitakere': 'auckland',
  'north shore': 'auckland',
  'papakura': 'auckland',
  'pukekohe': 'auckland',
  // Waikato
  'hamilton': 'waikato',
  'cambridge': 'waikato',
  'te awamutu': 'waikato',
  'matamata': 'waikato',
  'morrinsville': 'waikato',
  'tokoroa': 'waikato',
  'thames': 'waikato',
  // Bay of Plenty
  'tauranga': 'bay of plenty',
  'rotorua': 'bay of plenty',
  'whakatane': 'bay of plenty',
  'mount maunganui': 'bay of plenty',
  'te puke': 'bay of plenty',
  // Gisborne
  'gisborne': 'gisborne',
  // Hawkes Bay
  'napier': 'hawkes bay',
  'hastings': 'hawkes bay',
  'havelock north': 'hawkes bay',
  // Taranaki
  'new plymouth': 'taranaki',
  'stratford': 'taranaki',
  'hawera': 'taranaki',
  // Manawatu-Whanganui
  'palmerston north': 'manawatu-whanganui',
  'whanganui': 'manawatu-whanganui',
  'wanganui': 'manawatu-whanganui',
  'levin': 'manawatu-whanganui',
  'feilding': 'manawatu-whanganui',
  // Wellington
  'wellington': 'wellington',
  'lower hutt': 'wellington',
  'upper hutt': 'wellington',
  'porirua': 'wellington',
  'kapiti': 'wellington',
  'paraparaumu': 'wellington',
  // Northland
  'whangarei': 'northland',
  'kerikeri': 'northland',
  'kaitaia': 'northland',
  'dargaville': 'northland',
  // Nelson / Tasman
  'nelson': 'nelson',
  'richmond': 'tasman',
  'motueka': 'tasman',
  // Marlborough
  'blenheim': 'marlborough',
  'picton': 'marlborough',
  // West Coast
  'greymouth': 'west coast',
  'hokitika': 'west coast',
  'westport': 'west coast',
  // Canterbury
  'christchurch': 'canterbury',
  'ashburton': 'canterbury',
  'timaru': 'canterbury',
  'rangiora': 'canterbury',
  'kaiapoi': 'canterbury',
  'rolleston': 'canterbury',
  // Otago
  'dunedin': 'otago',
  'queenstown': 'otago',
  'wanaka': 'otago',
  'oamaru': 'otago',
  'alexandra': 'otago',
  'cromwell': 'otago',
  // Southland
  'invercargill': 'southland',
  'gore': 'southland',
  'te anau': 'southland',
}

// Resolve a location string to its normalised region name
function resolveRegion(location: string): string | null {
  const lower = location.toLowerCase().trim()
  // Direct city-to-region lookup
  if (NZ_CITY_TO_REGION[lower]) return NZ_CITY_TO_REGION[lower]
  // Check if it already is a region name (values in the map)
  const regionValues = new Set(Object.values(NZ_CITY_TO_REGION))
  if (regionValues.has(lower)) return lower
  return null
}

// Check if two location strings match (region-aware)
function locationsMatch(filterLocation: string, listingLocation: string): boolean {
  const filterLower = filterLocation.toLowerCase().trim()
  const listingLower = listingLocation.toLowerCase().trim()

  // Direct substring match (existing behaviour)
  if (listingLower.includes(filterLower) || filterLower.includes(listingLower)) {
    return true
  }

  // Region-aware matching: resolve both to regions and compare
  const filterRegion = resolveRegion(filterLower)
  const listingRegion = resolveRegion(listingLower)

  if (filterRegion && listingRegion && filterRegion === listingRegion) {
    return true
  }

  return false
}

// ── Relevance scoring for search results ──
export function computeRelevanceScore(
  listing: Listing,
  searchQuery: string
): number {
  if (!searchQuery) return 0

  const query = searchQuery.toLowerCase().trim()
  const title = listing.title.toLowerCase()
  const description = listing.description.toLowerCase()
  let score = 0

  // Exact title match: 100 points
  if (title === query) {
    score += 100
  }
  // Title starts with query: 50 points
  else if (title.startsWith(query)) {
    score += 50
  }
  // Title contains query as whole word: 30 points
  else if (new RegExp(`\\b${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(title)) {
    score += 30
  }
  // Title contains query as substring: 20 points
  else if (title.includes(query)) {
    score += 20
  }

  // Description contains query: 10 points
  if (description.includes(query)) {
    score += 10
  }

  // Category name matches: 15 points
  const category = getCategoryById(listing.category)
  if (category && category.name.toLowerCase().includes(query)) {
    score += 15
  }

  // Brand/make matches: 15 points
  const brand = listing.brand?.toLowerCase()
  if (brand && brand.includes(query)) {
    score += 15
  }
  if (listing.listingType === 'vehicle') {
    const vehicleListing = listing as Listing & { make?: string }
    if (vehicleListing.make?.toLowerCase().includes(query)) {
      score += 15
    }
  }

  return score
}

// Get a single listing by ID
export async function getListing(id: string): Promise<Listing | null> {
  const docRef = doc(db, LISTINGS_COLLECTION, id)
  const docSnap = await getDoc(docRef)

  if (!docSnap.exists()) return null

  return { id: docSnap.id, ...docSnap.data() } as Listing
}

// Get listings with optional filters
export async function getListings(options?: {
  status?: ListingStatus
  category?: string
  sellerId?: string
  limitCount?: number
  startAfterDoc?: DocumentSnapshot
}): Promise<Listing[]> {
  const constraints = []

  // Default to active listings
  if (options?.status) {
    constraints.push(where('status', '==', options.status))
  } else {
    constraints.push(where('status', '==', 'active'))
  }

  if (options?.category) {
    constraints.push(where('category', '==', options.category))
  }

  if (options?.sellerId) {
    constraints.push(where('sellerId', '==', options.sellerId))
  }

  // Note: orderBy requires a composite index. If the index is missing,
  // the query silently returns empty results. Sort client-side as fallback.
  // constraints.push(orderBy('createdAt', 'desc'))

  if (options?.limitCount) {
    constraints.push(limit(options.limitCount))
  }

  if (options?.startAfterDoc) {
    constraints.push(startAfter(options.startAfterDoc))
  }

  const q = query(collection(db, LISTINGS_COLLECTION), ...constraints)
  const snapshot = await getDocs(q)

  const results = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Listing[]

  // Client-side sort since composite index may not be deployed
  results.sort((a, b) => {
    const aTime = a.createdAt?.toMillis?.() || 0
    const bTime = b.createdAt?.toMillis?.() || 0
    return bTime - aTime
  })

  return results
}

// Subscribe to listings (real-time updates)
export function subscribeToListings(
  callback: (listings: Listing[]) => void,
  options?: {
    status?: ListingStatus
    category?: string
    sellerId?: string
    allStatuses?: boolean
  },
  onError?: (error: Error) => void
): Unsubscribe {
  const constraints = []

  if (options?.allStatuses) {
    // Don't filter by status — return all
  } else if (options?.status) {
    constraints.push(where('status', '==', options.status))
  } else {
    constraints.push(where('status', '==', 'active'))
  }

  if (options?.category) {
    constraints.push(where('category', '==', options.category))
  }

  if (options?.sellerId) {
    constraints.push(where('sellerId', '==', options.sellerId))
  }

  // orderBy removed — composite index may not be deployed, sort client-side
  const q = query(collection(db, LISTINGS_COLLECTION), ...constraints)

  return onSnapshot(
    q,
    (snapshot) => {
      const listings = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Listing[]

      // Client-side sort
      listings.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0
        const bTime = b.createdAt?.toMillis?.() || 0
        return bTime - aTime
      })

      callback(listings)
    },
    (error) => {
      console.error('Firestore subscription error:', error)
      onError?.(error)
    }
  )
}

// Create a new listing
// Strip undefined values — Firestore rejects them in addDoc/updateDoc
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as T
}

export async function createListing(
  data: CreateListingData,
  userId: string,
  userName: string,
  userVerified: boolean = false
): Promise<string> {
  const listingData = stripUndefined({
    ...data,
    sellerId: userId,
    sellerName: userName,
    sellerVerified: userVerified,
    status: 'active' as ListingStatus,
    views: 0,
    createdAt: serverTimestamp(),
  })

  const docRef = await addDoc(collection(db, LISTINGS_COLLECTION), listingData)

  // Increment user listing counts
  const currentMonth = new Date().toISOString().slice(0, 7)
  const userRef = doc(db, 'users', userId)
  await updateDoc(userRef, {
    listingCount: increment(1),
    monthlyListingCount: increment(1),
    monthlyListingReset: currentMonth,
  })

  return docRef.id
}

// Update a listing
export async function updateListing(
  id: string,
  data: Partial<Listing>
): Promise<void> {
  const docRef = doc(db, LISTINGS_COLLECTION, id)
  await updateDoc(docRef, stripUndefined({
    ...data,
    updatedAt: serverTimestamp(),
  }))
}

// Delete a listing
export async function deleteListing(id: string): Promise<void> {
  const docRef = doc(db, LISTINGS_COLLECTION, id)
  await deleteDoc(docRef)
}

// Increment view count
export async function incrementListingViews(id: string): Promise<void> {
  const docRef = doc(db, LISTINGS_COLLECTION, id)
  await updateDoc(docRef, {
    views: increment(1),
  })
}

// Get listings by IDs (for saved items)
export async function getListingsByIds(ids: string[]): Promise<Listing[]> {
  if (ids.length === 0) return []

  const listings: Listing[] = []

  // Firestore 'in' queries are limited to 10 items
  const chunks = []
  for (let i = 0; i < ids.length; i += 10) {
    chunks.push(ids.slice(i, i + 10))
  }

  for (const chunk of chunks) {
    const q = query(
      collection(db, LISTINGS_COLLECTION),
      where('__name__', 'in', chunk)
    )
    const snapshot = await getDocs(q)
    listings.push(
      ...snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Listing[]
    )
  }

  return listings
}

// Synonym map for vehicle body-type quick filters. Key is the canonical filter
// value (lowercase); values are substrings to match against the listing's free-
// text bodyType field. Keeps the filter robust to user-entered variants like
// "SUV" vs "Sport Utility" vs "4WD".
const BODY_TYPE_ALIASES: Record<string, string[]> = {
  car: ['car', 'sedan', 'hatch', 'coupe', 'wagon', 'convertible'],
  suv: ['suv', '4wd', '4x4', 'crossover', 'sport utility'],
  ute: ['ute', 'pickup', 'pick-up', 'pick up'],
  van: ['van', 'minivan', 'people mover', 'people-mover'],
  truck: ['truck', 'lorry', 'heavy'],
}

// Search listings (client-side filtering for now)
export function filterListings(
  listings: Listing[],
  filters: {
    query?: string
    category?: string
    group?: string
    location?: string
    priceMin?: number
    priceMax?: number
    condition?: string
    saleType?: string
    // Vehicle filters
    make?: string
    model?: string
    yearMin?: number
    yearMax?: number
    transmission?: string
    bodyType?: string
  }
): Listing[] {
  return listings.filter((listing) => {
    // Text search (title and description)
    if (filters.query) {
      const searchTerm = filters.query.toLowerCase()
      const titleMatch = listing.title.toLowerCase().includes(searchTerm)
      const descMatch = listing.description.toLowerCase().includes(searchTerm)
      const sellerMatch = listing.sellerName?.toLowerCase().includes(searchTerm)
      if (!titleMatch && !descMatch && !sellerMatch) return false
    }

    // Category filter
    if (filters.category && listing.category !== filters.category) {
      return false
    }

    // Category group filter (check if listing category belongs to group)
    if (filters.group) {
      const category = getCategoryById(listing.category)
      if (!category || category.group !== filters.group) return false
    }

    // Location filter (region-aware matching with NZ city/region lookup)
    if (filters.location && listing.location) {
      if (!locationsMatch(filters.location, listing.location)) {
        return false
      }
    } else if (filters.location && !listing.location) {
      return false
    }

    // Price range (explicit numeric coercion for safety)
    if (filters.priceMin !== undefined && listing.price !== null) {
      if (listing.price < Number(filters.priceMin)) return false
    }
    if (filters.priceMax !== undefined && listing.price !== null) {
      if (listing.price > Number(filters.priceMax)) return false
    }

    // Condition filter
    if (filters.condition && listing.condition !== filters.condition) {
      return false
    }

    // Sale type filter
    if (filters.saleType && listing.saleType !== filters.saleType) {
      return false
    }

    // Vehicle-specific filters
    if (listing.listingType === 'vehicle') {
      const vehicleListing = listing as Listing & {
        make?: string
        model?: string
        year?: number
        transmission?: string
        bodyType?: string
      }

      if (filters.make && !vehicleListing.make?.toLowerCase().includes(filters.make.toLowerCase())) {
        return false
      }
      if (filters.model && !vehicleListing.model?.toLowerCase().includes(filters.model.toLowerCase())) {
        return false
      }
      if (filters.yearMin && vehicleListing.year && vehicleListing.year < filters.yearMin) {
        return false
      }
      if (filters.yearMax && vehicleListing.year && vehicleListing.year > filters.yearMax) {
        return false
      }
      if (filters.transmission && vehicleListing.transmission !== filters.transmission) {
        return false
      }
      // Body type is a free-text field — use synonym map for robust matching
      if (filters.bodyType) {
        const aliases = BODY_TYPE_ALIASES[filters.bodyType.toLowerCase()] || [filters.bodyType.toLowerCase()]
        const value = vehicleListing.bodyType?.toLowerCase() || ''
        if (!value || !aliases.some((a) => value.includes(a))) {
          return false
        }
      }
    }

    return true
  })
}

// Bulk update listing status (batched writes, max 500 per batch)
export async function bulkUpdateListingStatus(
  listingIds: string[],
  status: ListingStatus
): Promise<void> {
  // Firestore batches are limited to 500 operations
  const chunks: string[][] = []
  for (let i = 0; i < listingIds.length; i += 500) {
    chunks.push(listingIds.slice(i, i + 500))
  }

  for (const chunk of chunks) {
    const batch = writeBatch(db)
    for (const id of chunk) {
      const ref = doc(db, LISTINGS_COLLECTION, id)
      batch.update(ref, { status, updatedAt: serverTimestamp() })
    }
    await batch.commit()
  }
}

// Bulk delete listings (batched writes, max 500 per batch)
export async function bulkDeleteListings(listingIds: string[]): Promise<void> {
  const chunks: string[][] = []
  for (let i = 0; i < listingIds.length; i += 500) {
    chunks.push(listingIds.slice(i, i + 500))
  }

  for (const chunk of chunks) {
    const batch = writeBatch(db)
    for (const id of chunk) {
      const ref = doc(db, LISTINGS_COLLECTION, id)
      batch.delete(ref)
    }
    await batch.commit()
  }
}

// Sort listings
export function sortListings(
  listings: Listing[],
  sortBy: 'newest' | 'oldest' | 'price-low' | 'price-high' | 'ending-soon' | 'relevance',
  searchQuery?: string
): Listing[] {
  const sorted = [...listings]

  switch (sortBy) {
    case 'relevance': {
      if (!searchQuery) {
        // Fall back to newest if no query
        return sorted.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0
          const bTime = b.createdAt?.toMillis?.() || 0
          return bTime - aTime
        })
      }
      return sorted.sort((a, b) => {
        const aScore = computeRelevanceScore(a, searchQuery)
        const bScore = computeRelevanceScore(b, searchQuery)
        if (bScore !== aScore) return bScore - aScore
        // Tie-break by newest
        const aTime = a.createdAt?.toMillis?.() || 0
        const bTime = b.createdAt?.toMillis?.() || 0
        return bTime - aTime
      })
    }

    case 'newest':
      return sorted.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0
        const bTime = b.createdAt?.toMillis?.() || 0
        return bTime - aTime
      })

    case 'oldest':
      return sorted.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0
        const bTime = b.createdAt?.toMillis?.() || 0
        return aTime - bTime
      })

    case 'price-low':
      return sorted.sort((a, b) => {
        const aPrice = a.price ?? Infinity
        const bPrice = b.price ?? Infinity
        return aPrice - bPrice
      })

    case 'price-high':
      return sorted.sort((a, b) => {
        const aPrice = a.price ?? -Infinity
        const bPrice = b.price ?? -Infinity
        return bPrice - aPrice
      })

    case 'ending-soon':
      return sorted.sort((a, b) => {
        const aEnd = a.expiresAt?.toMillis?.() || Infinity
        const bEnd = b.expiresAt?.toMillis?.() || Infinity
        return aEnd - bEnd
      })

    default:
      return sorted
  }
}
